import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { getUserAlerts } from '../../../services/propertyAlertsService';
import { wishlistService } from '../../../services/wishlistService';

const PersonalizedHero = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();

  const [alerts, setAlerts] = useState([]);
  const [savedProperties, setSavedProperties] = useState([]);
  const [recentViews, setRecentViews] = useState([]);
  const [loading, setLoading] = useState(true);

  const firstName = userProfile?.full_name?.split(' ')?.[0] || user?.email?.split('@')?.[0] || 'vous';

  useEffect(() => {
    if (!user?.id) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const [alertsRes, wishlistRes] = await Promise.all([
          getUserAlerts(user?.id),
          wishlistService?.getWishlist(user?.id)
        ]);

        setAlerts(alertsRes?.data?.filter((a) => a?.is_active)?.slice(0, 3) || []);
        setSavedProperties(wishlistRes?.data?.slice(0, 3) || []);

        // Load recent views from localStorage
        const stored = localStorage.getItem(`recent_views_${user?.id}`);
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            setRecentViews(Array.isArray(parsed) ? parsed?.slice(0, 3) : []);
          } catch (_) {
            setRecentViews([]);
          }
        }
      } catch (err) {
        console.error('PersonalizedHero fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.id]);

  const activeAlertsCount = alerts?.length;
  const savedCount = savedProperties?.length;
  const recentCount = recentViews?.length;

  return (
    <section className="relative min-h-[92vh] flex flex-col items-center justify-center overflow-hidden border-none outline-none">
      
      {/* 
        Background 
        Le fond est transparent car c'est Homepage.jsx qui gère le noir #050505 et les halos.
        Nous ajoutons juste un halo supplémentaire centré pour mettre en valeur les cartes.
      */}
      <div className="absolute inset-0 z-0 pointer-events-none flex items-center justify-center">
        <div className="w-[800px] h-[500px] bg-brand-gold/5 rounded-full blur-[150px]" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 lg:px-8 pt-24 pb-16 flex flex-col items-center">
        
        {/* Greeting (Apple Style Typography) */}
        <div className="mb-14 text-center">
          <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-md rounded-full px-5 py-2 mb-6 border-none shadow-lg">
            <Icon name="Sparkles" size={16} className="text-brand-gold" />
            <span className="text-brand-gold/90 text-xs font-bold tracking-widest uppercase">Espace Privé</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-heading font-bold text-white mb-6 leading-tight tracking-tighter">
            Bonjour, <br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-gold via-yellow-200 to-brand-gold capitalize">
              {firstName}.
            </span>
          </h1>
          <p className="text-white/50 text-lg md:text-xl max-w-2xl mx-auto font-light leading-relaxed">
            Reprenez là où vous en étiez. Consultez vos propriétés sauvegardées, vos alertes, et continuez vos recherches.
          </p>
        </div>

        {/* Dashboard Cards Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="w-12 h-12 border-2 border-white/20 border-t-brand-gold rounded-full animate-spin" />
          </div>
        ) : (
          <div className="w-full">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto mb-16">

              {/* Active Alerts */}
              <Link
                to="/user-profile"
                className="group bg-[#111111] rounded-3xl p-8 transition-transform duration-500 hover:-translate-y-2 cursor-pointer shadow-2xl border-none outline-none flex flex-col"
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="w-14 h-14 rounded-2xl bg-brand-gold/10 flex items-center justify-center shadow-inner">
                    <Icon name="Bell" size={24} className="text-brand-gold" />
                  </div>
                  {activeAlertsCount > 0 && (
                    <span className="bg-brand-gold text-black text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                      {activeAlertsCount}
                    </span>
                  )}
                </div>
                
                <h3 className="text-white font-heading font-bold text-xl mb-3 group-hover:text-brand-gold transition-colors">
                  Mes Alertes
                </h3>
                
                {activeAlertsCount > 0 ? (
                  <div className="space-y-3 mt-auto">
                    {alerts?.map((alert) => (
                      <div key={alert?.id} className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0 animate-pulse" />
                        <span className="text-white/60 text-sm font-light truncate">{alert?.name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/40 text-sm font-light mt-auto">Aucune alerte configurée</p>
                )}
              </Link>

              {/* Saved Properties */}
              <Link
                to="/user-profile"
                className="group bg-[#111111] rounded-3xl p-8 transition-transform duration-500 hover:-translate-y-2 cursor-pointer shadow-2xl border-none outline-none flex flex-col"
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="w-14 h-14 rounded-2xl bg-red-500/10 flex items-center justify-center shadow-inner">
                    <Icon name="Heart" size={24} className="text-red-400" />
                  </div>
                  {savedCount > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg shadow-red-500/20">
                      {savedCount}
                    </span>
                  )}
                </div>
                
                <h3 className="text-white font-heading font-bold text-xl mb-3 group-hover:text-red-400 transition-colors">
                  Favoris
                </h3>
                
                {savedCount > 0 ? (
                  <div className="space-y-3 mt-auto">
                    {savedProperties?.map((prop) => (
                      <div key={prop?.id} className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
                        <span className="text-white/60 text-sm font-light truncate">
                          {prop?.property_title || 'Propriété'}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/40 text-sm font-light mt-auto">Aucun bien sauvegardé</p>
                )}
              </Link>

              {/* Recent Views */}
              <Link
                to="/property-search-listings"
                className="group bg-[#111111] rounded-3xl p-8 transition-transform duration-500 hover:-translate-y-2 cursor-pointer shadow-2xl border-none outline-none flex flex-col sm:col-span-2 lg:col-span-1"
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="w-14 h-14 rounded-2xl bg-blue-500/10 flex items-center justify-center shadow-inner">
                    <Icon name="Clock" size={24} className="text-blue-400" />
                  </div>
                  {recentCount > 0 && (
                    <span className="bg-blue-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg shadow-blue-500/20">
                      {recentCount}
                    </span>
                  )}
                </div>
                
                <h3 className="text-white font-heading font-bold text-xl mb-3 group-hover:text-blue-400 transition-colors">
                  Vues Récentes
                </h3>
                
                {recentCount > 0 ? (
                  <div className="space-y-3 mt-auto">
                    {recentViews?.map((view, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />
                        <span className="text-white/60 text-sm font-light truncate">
                          {view?.title || view?.property_title || 'Propriété'}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/40 text-sm font-light mt-auto">Historique vide</p>
                )}
              </Link>

            </div>
          </div>
        )}

        {/* Bottom Actions & Stats */}
        <div className="w-full max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8 bg-white/5 rounded-3xl p-8 shadow-2xl border-none">
          
          <div className="flex gap-4">
            <Link to="/property-search-listings">
              <Button
                variant="default"
                size="lg"
                iconName="Search"
                iconPosition="left"
                className="bg-white text-black hover:bg-gray-200 rounded-full font-bold px-8 shadow-lg border-none hover:scale-105 transition-transform"
              >
                Explorer
              </Button>
            </Link>
            <Link to="/user-profile">
              <Button
                variant="outline"
                size="lg"
                iconName="User"
                iconPosition="left"
                className="bg-white/5 text-white hover:bg-white/10 rounded-full font-medium px-8 border-none"
              >
                Profil
              </Button>
            </Link>
          </div>

          {/* Stats rapides horizontales */}
          <div className="flex gap-8 text-center md:text-left">
            <div>
              <div className="text-2xl font-heading font-bold text-brand-gold leading-none mb-1">{activeAlertsCount}</div>
              <div className="text-white/40 text-xs font-bold uppercase tracking-widest">Alertes</div>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div>
              <div className="text-2xl font-heading font-bold text-red-400 leading-none mb-1">{savedCount}</div>
              <div className="text-white/40 text-xs font-bold uppercase tracking-widest">Favoris</div>
            </div>
          </div>

        </div>

      </div>

      {/* Scroll Indicator Subtil */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 opacity-50">
        <div className="animate-bounce">
          <Icon name="ChevronDown" size={24} color="white" />
        </div>
      </div>
    </section>
  );
};

export default PersonalizedHero;